import React, { useState, useEffect } from "react";
import { useTheme } from "styled-components";
import { toast } from "react-toastify";
import api from "../../services/api";
import { Container } from "./styles";
import CertificatesLoadingSkeleton from "./CertificatesLoadingSkeleton";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import {
  FiPlus,
  FiUploadCloud,
  FiTrash2,
  FiSearch,
  FiAlertTriangle,
  FiCalendar,
  FiShield,
} from "react-icons/fi";
import {
  GoShield,
  GoShieldX,
  GoAlert,
  GoCheck,
} from "react-icons/go";
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineXCircle,
} from "react-icons/hi";

export default function Certificates() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCertificates();
    // Refresh list when modal closes (after upload)
    const handler = () => fetchCertificates();
    window.addEventListener('certificates-updated', handler);
    return () => window.removeEventListener('certificates-updated', handler);
  }, []);

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const selectedProjectId = localStorage.getItem('vidal_selected_project');
      if (!selectedProjectId) {
        toast.error('Nenhum projeto selecionado');
        setLoading(false);
        return;
      }

      const response = await api.get(`/certificates/list/${selectedProjectId}`);

      if (response.data.success) {
        setCertificates(response.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar certificados:', error);
      toast.error('Erro ao carregar certificados');
    } finally {
      setLoading(false);
    }
  };

  // KPIs
  const totalCertificates = certificates.length;
  const now = new Date();
  const validCertificates = certificates.filter(
    (c) =>
      new Date(c.validTo) > now &&
      Math.floor((new Date(c.validTo) - now) / 86400000) > 30
  ).length;
  const expiredCertificates = certificates.filter(
    (c) => new Date(c.validTo) <= now
  ).length;
  const expiringCertificates = certificates.filter((c) => {
    const days = Math.floor((new Date(c.validTo) - now) / 86400000);
    return days > 0 && days <= 30;
  }).length;

  // Status helper
  const getCertStatus = (cert) => {
    const days = Math.floor((new Date(cert.validTo) - now) / 86400000);
    if (days < 0) return { key: 'danger', label: 'Expirado', days };
    if (days <= 30) return { key: 'warn', label: `${days}d restantes`, days };
    return { key: 'ok', label: 'Válido', days };
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('pt-BR') : 'N/A';

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    const d = cnpj.replace(/\D/g, '');
    if (d.length !== 14) return cnpj;
    return d.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  };

  const getDisplayName = (cert) => {
    const raw = cert.companyName || cert.commonName || cert.nome || '';
    return raw.includes(':')
      ? raw.split(':')[0]
      : raw || 'Certificado Digital';
  };

  // Bold commercial color palette (10 saturated solids, no yellow/amber)
  // White text on saturated mid-luminance backgrounds for strong contrast.
  const TAG_PALETTE = [
    '#dc2626', // red
    '#ea580c', // orange
    '#65a30d', // lime (darker for white-text contrast)
    '#16a34a', // green
    '#0d9488', // teal
    '#0891b2', // cyan
    '#2563eb', // blue
    '#4f46e5', // indigo
    '#9333ea', // purple
    '#db2777', // pink
  ];

  // Color is bound to the text (cidade/estado/bairro), so the same
  // text always produces the same color across the entire list.
  const getColorFromText = (text) => {
    const seed = (text || '').toString().trim().toLowerCase();
    if (!seed) return TAG_PALETTE[0];
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    return TAG_PALETTE[Math.abs(hash) % TAG_PALETTE.length];
  };

  // Backward-compat shim (kept for any caller using the previous name).
  const getTagColor = (cert) => getColorFromText(cert?.city || cert?.cnpj || '');

  // Filtering
  const filtered = certificates.filter((cert) => {
    const name = getDisplayName(cert).toLowerCase();
    const cnpj = cert.cnpj || '';
    const matchSearch =
      name.includes(searchTerm.toLowerCase()) || cnpj.includes(searchTerm);

    const s = getCertStatus(cert);
    if (statusFilter === 'valid') return matchSearch && s.key === 'ok';
    if (statusFilter === 'expiring') return matchSearch && s.key === 'warn';
    if (statusFilter === 'expired') return matchSearch && s.key === 'danger';
    return matchSearch;
  });

  // Sort: expired first, then expiring, then valid
  const sorted = [...filtered].sort((a, b) => {
    const sa = getCertStatus(a);
    const sb = getCertStatus(b);
    const order = { danger: 0, warn: 1, ok: 2 };
    return order[sa.key] - order[sb.key] || sa.days - sb.days;
  });

  // Delete
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      const response = await api.delete(
        `/certificates/delete/${projectId}/${deleteTarget.cnpj}`
      );
      if (response.data.success) {
        toast.success('Certificado excluído');
        setDeleteTarget(null);
        fetchCertificates();
      }
    } catch (error) {
      toast.error('Erro ao excluir certificado');
    } finally {
      setDeleting(false);
    }
  };

  // Open CertificateModal (from App.js) to upload new cert
  const openUploadModal = () => {
    window.dispatchEvent(new CustomEvent('open-certificate-modal'));
  };

  if (loading) {
    return <CertificatesLoadingSkeleton isDark={theme.title === 'dark'} />;
  }

  return (
    <Container>
      {/* HEADER */}
      <div className="header-info">
        <div className="header-details">
          <h3>Certificados Digitais</h3>
          <div className="header-meta">
            <span className="status-tag">SISTEMA ATIVO</span>
            <span className="update-tag">
              {totalCertificates > 0
                ? `${totalCertificates} cadastrado${totalCertificates !== 1 ? 's' : ''}`
                : 'nenhum certificado cadastrado'}
            </span>
          </div>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="kpi-cards">
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb"><GoShield /></div>
            <div className="text">
              <p>{totalCertificates}</p>
              <p>Total de Certificados</p>
            </div>
          </div>
        </div>

        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><HiOutlineCheckCircle /></div>
            <div className="text">
              <p>{validCertificates}</p>
              <p>Válidos</p>
            </div>
          </div>
        </div>

        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb"><HiOutlineExclamationCircle /></div>
            <div className="text">
              <p>{expiringCertificates}</p>
              <p>Expirando em 30 dias</p>
            </div>
          </div>
        </div>

        <div className="kpi-card danger">
          <div className="card-content">
            <div className="thumb"><HiOutlineXCircle /></div>
            <div className="text">
              <p>{expiredCertificates}</p>
              <p>Expirados</p>
            </div>
          </div>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="toolbar">
        <div className="toolbar-left">
          <button className="btn-new" onClick={openUploadModal}>
            <FiPlus />
            Novo Certificado
          </button>
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar por nome ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="toolbar-right">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'valid', label: 'Válidos' },
            { id: 'expiring', label: 'Expirando' },
            { id: 'expired', label: 'Expirados' },
          ].map((f) => (
            <button
              key={f.id}
              className={`filter-pill ${statusFilter === f.id ? 'active' : ''}`}
              onClick={() => setStatusFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* EXPIRED BANNER */}
      {expiredCertificates > 0 && statusFilter === 'all' && (
        <div className="expired-banner">
          <div className="banner-icon">
            <FiAlertTriangle />
          </div>
          <div className="banner-text">
            <h4>
              {expiredCertificates} certificado
              {expiredCertificates > 1 ? 's' : ''} expirado
              {expiredCertificates > 1 ? 's' : ''}
            </h4>
            <p>
              O sistema não consegue consultar a SEFAZ para empresas com certificados vencidos.
            </p>
          </div>
          <button className="banner-action" onClick={openUploadModal}>
            <FiUploadCloud />
            Atualizar
          </button>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div className="main-content">
        {sorted.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <FiShield />
            </div>
            <h4>
              {searchTerm || statusFilter !== 'all'
                ? 'Nenhum certificado encontrado'
                : 'Nenhum certificado cadastrado'}
            </h4>
            <p>
              {searchTerm || statusFilter !== 'all'
                ? 'Ajuste os filtros ou termo de busca'
                : 'Faça upload de certificados digitais para começar'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button className="empty-cta" onClick={openUploadModal}>
                <FiUploadCloud />
                Enviar Certificado
              </button>
            )}
          </div>
        ) : (
          <div className="cert-grid">
            {sorted.map((cert) => {
              const status = getCertStatus(cert);
              const displayName = getDisplayName(cert);
              const StatusIcon =
                status.key === 'ok'
                  ? GoCheck
                  : status.key === 'warn'
                  ? GoAlert
                  : GoShieldX;

              return (
                <div key={cert._id || cert.cnpj} className={`cert-card ${status.key}`}>
                  <div className="cert-actions">
                    <button
                      className="btn-delete"
                      onClick={() => setDeleteTarget(cert)}
                      title="Excluir certificado"
                    >
                      <FiTrash2 />
                    </button>
                  </div>

                  <div className="cert-top">
                    <span className="cert-eyebrow">
                      CNPJ · {formatCNPJ(cert.cnpj)}
                    </span>
                    <h5 title={displayName}>{displayName.toLowerCase()}</h5>
                    {(cert.city || cert.state || cert.neighborhood) && (
                      <div className="cert-location">
                        {cert.city && (
                          <span
                            className="location-tag"
                            style={{ background: getColorFromText(cert.city) }}
                          >
                            {cert.city.toLowerCase()}
                          </span>
                        )}
                        {cert.state && (
                          <span className="location-tag neutral">
                            {cert.state}
                          </span>
                        )}
                        {cert.neighborhood && (
                          <span className="location-tag neutral">
                            {cert.neighborhood.toLowerCase()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="cert-footer">
                    <div className="cert-validity">
                      <FiCalendar />
                      <span>até {formatDate(cert.validTo)}</span>
                    </div>
                    <span className={`status-pill ${status.key}`}>
                      <StatusIcon />
                      {status.label}
                    </span>
                  </div>

                  {(status.key === 'danger' || status.key === 'warn') && (
                    <button
                      className={`cert-renew-btn ${status.key === 'warn' ? 'warning' : 'danger'}`}
                      onClick={openUploadModal}
                    >
                      <FiUploadCloud />
                      {status.key === 'danger'
                        ? 'Enviar novo certificado'
                        : 'Renovar certificado'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* DELETE MODAL */}
      <ConfirmDeleteModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Certificado"
        description={`Deseja excluir o certificado de ${
          deleteTarget ? getDisplayName(deleteTarget) : ''
        }? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        loading={deleting}
      />
    </Container>
  );
}
